import React from 'react';
import { Dispatch } from 'redux';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { AppState, BookingsFilter, filterBookingsOnType } from 'acscp/store';
import { isBookingRead } from 'acscp/store/helpers/isBookingRead';
import style from './tripCounter.module.scss';
import classNames from 'classnames';
import { formatBookingsFilter } from 'acscp/components/common/helpers/formatBookingFilter';
import { FetchRequest } from 'acscp/actions';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { LoadingIndicator } from 'acscp/components/common/loadingIndicator';
import { BookingsRequest, RequestClass } from 'acscp/api/requests';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { getDomIdForFilter } from '../common/helpers/getDomIDs';
import { HashLink } from 'react-router-hash-link';
import { chartersList } from 'acscp/components/routeHelper';

interface TripCounterStateProps {
    infos: BookingsInformation[];
    bookingsRequestState: RequestStateProps;
}

interface TripCounterDispatchProps {
    fetchBookings: () => void;
}

interface TripCounterOwnProps {
    onSamePage: boolean;
}

class BookingsInformation {
    count: number;
    unread: boolean;

    constructor(public filter: BookingsFilter, state: AppState) {
        const bookings = filterBookingsOnType(state.bookingsDetails, filter);
        this.count = bookings.length;
        this.unread = bookings.some(b => !isBookingRead(b, state.bookingsReadDetails));
    }
}

export class TripCounterPresentational
    extends PresentationComponentBase<TripCounterOwnProps, TripCounterStateProps, TripCounterDispatchProps, {}> {

    componentDidMount(): void {
        this.props.fetchBookings();
    }

    static buildToLink(id: string): string {
        return chartersList(id);
    }

    static handleScroll(id: string): void {
        const target = document.getElementById(id);
        if (target !== null) {
            target.scrollIntoView();
        }
    }

    generateFilterLink(onSamePage: boolean, info: BookingsInformation, key: number, classes: string): JSX.Element {
        const formattedFilter = formatBookingsFilter(info.filter);

        if (info.filter === BookingsFilter.Flown) {
            return (
                <HashLink
                    to={'/tripFlown'}
                    key={key}
                    className={classes}
                >
                    {formattedFilter}
                </HashLink>
            );
        }
        if (onSamePage) {
            return (
                <div
                    onClick={() => TripCounterPresentational.handleScroll(getDomIdForFilter(info.filter))}
                    key={key}
                    className={classes}
                >
                    {formattedFilter}: {info.count}
                </div>
            );
        } else {
            return (
                <HashLink
                    to={TripCounterPresentational.buildToLink(getDomIdForFilter(info.filter))}
                    key={key}
                    className={classes}
                >
                    {formattedFilter}: {info.count}
                </HashLink>
            );
        }
    }

    render(): JSX.Element {
        if (this.props.bookingsRequestState.isLoading) {
            return (<LoadingIndicator/>);
        }

        return (
            <div className={style.TripCounter}>
                {this.props.bookingsRequestState.isLoading
                    ? <LoadingIndicator />
                    : this.props.infos.map((info, i) => this.generateFilterLink(
                        this.props.onSamePage, info, i,
                        classNames(style.Count, {[style.Unread]: info.unread, [style.Enabled]: true})
                ))}
            </div>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: {}, getRequestError: (request: RequestClass<any>) => RequestStateProps
): TripCounterStateProps {
    const infos = [
        new BookingsInformation(BookingsFilter.Enquiries, state),
        new BookingsInformation(BookingsFilter.Quoted, state),
        new BookingsInformation(BookingsFilter.Booked, state),
        new BookingsInformation(BookingsFilter.Flown, state)
    ];
    return {
        infos,
        bookingsRequestState: getRequestError(BookingsRequest)
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: {}): TripCounterDispatchProps {
    return {
        fetchBookings: () => dispatch(new FetchRequest(new BookingsRequest()))
    };
}

export const TripCounter = connectWithRequests(mapStateToProps, mapDispatchToProps)(TripCounterPresentational);
